import {WedMgr} from "/@back@/edit/wed/wedEditor.js";
import {IChildWedlet, IParentWedlet, IRootWedlet, IWedlet} from "/@back@/edit/wed/wedlets/wedlet.js";
import {IEndPoint, IEndPointHolder, IPathResolver} from "/@lib@/commons/io/io.js";
import {IReg, REG} from "/@lib@/commons/registry.js";
import {DOM, ENodeType, EUnknownNodeType, INodeFilter} from "/@lib@/commons/xml/dom.js";
import {IJmlNode, IJmlObj, JML} from "/@lib@/commons/xml/jml.js";
import {ISkStructDef} from "/@lib@/edit/schema/schemaAnnots.js";
import {SkRuleElt, SkRuleNode} from "/@lib@/edit/schema/schemaPatterns.js";

/**
 * Un WedModel représente un fichier Wed (avec ses imports) et définit l'ensemble des modèles de Wedlets disponibles
 * pour gérer l'édition d'un flux xml.
 * Ce registre des IWedletModel est utilisé par un éditeur via son WedMgr.
 */
export class WedModel {

	/** Résultat de IWedSelector signifiant que le IWedletModel ne sera pas sélectionné. */
	static readonly SELECTOR_REJECT = 0;

	/** Résultat de IWedSelector signifiant que le IWedletModel peut de suite être retourné sans tester les autres IWedletModel potentiels. */
	static readonly SELECTOR_PERFECT_MATCH = 100;

	/**
	 * Modules JS déclarés dans ce Wed.
	 *
	 * Si le module exporte une méthode
	 *    jslibAsyncInit(jsEndPoint:IEndPoint, elt:Element, reg:Reg<any>):Promise<void>
	 * elle sera executée au chargement, et la promise résolue avant que le WED soit considéré disponible
	 * (chargement de free customElements, ajout de wedletModels...).
	 * Le reg est spécifique à ce model et isolé (ce n'est pas un SubReg), il peut être enrichi. Ses extPoints et env seront copiés
	 * à chaque initialisation d'un IWedEditor dans le IWedEditor.rootNode.reg
	 * Note : les reg des wed importés dans ce wed sont aussi récupérés en respectant les règles de levelAuthority des reg.
	 */
	jsLibs: Dict<any> = {};

	/** Registre du WedModel qui sera recopié à chaque instanciation d'un IWedEditor avec ce Wed. */
	reg: IReg<never> = new REG.Reg();

	/**
	 * Liste ordonnée des thèmes.
	 * Le 1er thème dont IWedTheme.themeContext match le thème courant issu du contexte est sélectionné.
	 * Le dernier thème de la liste ne devrait pas posséder de regexp pour être toujours sélectionné.
	 */
	themes: IWedTheme[] = [];

	protected docModel: ModelRef;
	protected genericEltModel: ModelRef;
	protected genericAttrModel: ModelRef;
	protected textModel: ModelRef;
	protected commentModel: ModelRef;
	protected eltModels: Map<string, ModelRef> = new Map();
	protected attrModels: Map<string, ModelRef> = new Map();

	//protected structModels: Map<string, ModelRef> = new Map();

	/**
	 *
	 * @param model
	 * @param nodeName Nom du noeud (si nodeType est ENodeType.element ou Node.ATTRIBUTE_NODE).
	 * @param priority Plus la priorité est élevée plus ce IWedletModel sera priviliégié.
	 * @param selector
	 */
	addNodeModel(model: IWedletModel, priority: number = 0, selector?: IWedSelector) {
		const newModel = new ModelRef(model, priority, selector);
		switch (model.nodeType) {
		case ENodeType.element:
			if (model.nodeName) this.eltModels.set(model.bindKey, WedModel.injectModel(newModel, this.eltModels.get(model.bindKey)));
			else this.genericEltModel = WedModel.injectModel(newModel, this.genericEltModel);
			break;
		case ENodeType.attribute:
			if (model.nodeName) this.attrModels.set(model.bindKey, WedModel.injectModel(newModel, this.attrModels.get(model.bindKey)));
			else this.genericAttrModel = WedModel.injectModel(newModel, this.genericAttrModel);
			break;
		case ENodeType.document:
		case ENodeType.documentFragment:
			this.docModel = WedModel.injectModel(newModel, this.docModel);
			break;
		case ENodeType.text:
			this.textModel = WedModel.injectModel(newModel, this.textModel);
			break;
		case ENodeType.comment:
			this.commentModel = WedModel.injectModel(newModel, this.commentModel);
			break;
		default:
			throw Error(`NodeType unknown : ${model.nodeType}`);
		}
	}

	// /**
	//  * Déclaration d'un IWedletModel par son name. Nécessaire pour les directives,
	//  * mais utilisable pour tout type de IWedletModel.
	//  */
	// addStructModel(model: IWedletModel, structName: string, priority: number = 0, selector?: IWedSelector) {
	//     this.structModels.set(structName, WedModel.injectModel(new ModelRef(model, priority, selector), this.structModels.get(structName)));
	//}
	// /** Recherche un IWedletModel par son name. */
	// findModelByName(structName: string, modes: string[], ctx?: WedMgr|IWedlet, selector?: IWedSelector): IWedletModel {
	//     return WedModel.findModel(this.structModels.get(structName), modes, ctx, selector);
	// }

	/** Recherche un IWedletModel pour un Document. */
	findModelForDocument(modes: string[], ctx?: WedMgr | IWedlet, selector?: IWedSelector, params?: any): IWedletModel {
		return WedModel.findModel(this.docModel, modes, null, ctx, selector, params);
	}

	/** Recherche un IWedDisplayElt pour un noeud. */
	findDisplayForNode(jmlNode: IJmlNode, childrenElt: IWedChildrenElt): IWedDisplayElt {
		if (childrenElt.wedDefaultDisplay) {
			if (WED.isDisplayMatchNode(childrenElt.wedDefaultDisplay, jmlNode)) return childrenElt.wedDefaultDisplay;
		}
		if (childrenElt.wedDisplays) {
			const displays = childrenElt.wedDisplays;
			for (let i = 0; i < displays.length; i++) {
				const display = displays[i];
				if (display.wedletModel) {
					if (WED.isModelMatchNode(display.wedletModel, jmlNode)) return display;
				} else {
					if (WED.isDisplayMatchNode(display, jmlNode)) return display;
				}
			}
		}
		return null;
	}

	findModelForNodeFromCh(jmlNode: IJmlNode, ctx: WedMgr | IWedlet, chidrenElt: IWedChildrenElt): IWedletModel {
		// if (chidrenElt.wedContainers) for (let i = 0; i < chidrenElt.wedContainers.length; i++) {
		// 	let wedCtn = chidrenElt.wedContainers[i];
		// 	if (wedCtn.wedMatcher.matchNode(jmlNode)) {
		// 		let chElt = WED.findChildrenEltForNode(wedCtn.wedChildrenElts, jmlNode);
		// 		return chElt ? this.findModelForNodeFromCh(jmlNode, ctx, chElt) : null;
		// 	}
		// }
		return this.findModelForNode(jmlNode, chidrenElt.wedModesNodes, chidrenElt.wedVariants, ctx, chidrenElt.wedSelector, chidrenElt.wedPreferedModels, chidrenElt.wedParams);
	}

	/** Recherche un IWedletModel pour un noeud quelquesoit son type (élément, texte, comment). */
	findModelForNode(jmlNode: IJmlNode, modes: Dict<string[]> | null, variants: Dict<string>, ctx?: WedMgr | IWedlet, selector?: IWedSelector, preferedModels?: IWedletModel[], params?: any): IWedletModel {
		if (preferedModels) {
			for (let i = 0; i < preferedModels.length; i++) {
				const model = preferedModels[i];
				if (WED.isModelMatchNode(model, jmlNode)) {
					if (!selector || selector(model, jmlNode, ctx, params) > WedModel.SELECTOR_REJECT) return model;
				}
			}
		}
		if (typeof jmlNode === 'string') return WedModel.findModel(this.textModel, WED.getModesForText(modes), jmlNode, ctx, selector, params);
		const name = jmlNode[''];
		if (name === JML.COMMENT) return WedModel.findModel(this.commentModel, WED.getModesForComment(modes), jmlNode, ctx, selector, params);
		const md = WED.getModesForName(modes, name);
		return WedModel.findModel(this.eltModels.get(variants ? variants[name] || name : name), md, jmlNode, ctx, selector, params) || WedModel.findModel(this.genericEltModel, md, jmlNode, ctx, selector, params);
	}

	/** Recherche un IWedDisplayElt pour un attribut. */
	findDisplayForAttr(attrName: string, childrenElt: IWedChildrenElt): IWedDisplayElt {
		if (childrenElt.wedDefaultDisplay) {
			if (WED.isDisplayMatchAttr(childrenElt.wedDefaultDisplay, attrName)) return childrenElt.wedDefaultDisplay;
		}
		if (childrenElt.wedDisplays) {
			const displays = childrenElt.wedDisplays;
			for (let i = 0; i < displays.length; i++) {
				const display = displays[i];
				if (display.wedletModel) {
					if (WED.isModelMatchAttr(display.wedletModel, attrName)) return display;
				} else {
					if (WED.isDisplayMatchAttr(display, attrName)) return display;
				}
			}
		}
		return null;
	}

	/** Recherche un IWedletModel pour un attribut. */
	findModelForAttr(attrName: string, attrValue: string, modes: Dict<string[]> | null, variants: Dict<string> | null, ctx?: WedMgr | IWedlet, selector?: IWedSelector, preferedModels?: IWedletModel[], params?: any): IWedletModel {
		if (preferedModels) {
			for (let i = 0; i < preferedModels.length; i++) {
				const model = preferedModels[i];
				if (WED.isModelMatchAttr(model, attrName)) {
					if (!selector || selector(model, attrValue, ctx, params) > WedModel.SELECTOR_REJECT) return model;
				}
			}
		}
		const md = WED.getModesForName(modes, attrName);
		return WedModel.findModel(this.attrModels.get(variants ? variants[attrName] || attrName : attrName), md, attrValue, ctx, selector, params) || WedModel.findModel(this.genericAttrModel, md, attrValue, ctx, selector, params);
	}

	/** Recherche un IWedletModel pour un contexte virtual. */
	findModelForVirtual(nodeType: ENodeType, nodeName: string | null, modes: Dict<string[]>, variants: Dict<string>, ctx: IWedlet, selector?: IWedSelector, preferedModels?: IWedletModel[], params?: any): IWedletModel {
		if (nodeType === ENodeType.attribute) {
			if (preferedModels) {
				for (let i = 0; i < preferedModels.length; i++) {
					const model = preferedModels[i];
					if (WED.isModelMatchAttr(model, nodeName)) {
						if (!selector || selector(model, null, ctx, params) > WedModel.SELECTOR_REJECT) return model;
					}
				}
			}
			const md = WED.getModesForName(modes, nodeName);
			if (nodeName) return WedModel.findModel(this.attrModels.get(variants ? variants[nodeName] || nodeName : nodeName), md, null, ctx, selector, params) || WedModel.findModel(this.genericAttrModel, md, null, ctx, selector, params);
			return WedModel.findModel(this.genericAttrModel, md, null, ctx, selector, params);
		} else {
			if (preferedModels) {
				for (let i = 0; i < preferedModels.length; i++) {
					const model = preferedModels[i];
					if (model.nodeType === nodeType && (!model.nodeName || model.nodeName === nodeName)) {
						if (!selector || selector(model, null, ctx, params) > WedModel.SELECTOR_REJECT) return model;
					}
				}
			}
			switch (nodeType) {
			case ENodeType.element:
				if (!nodeName) {
					//Name du defaultDisplay indéfini, on tente par le schéma de trouver le 1er possible.
					const docH = ctx.wedMgr.docHolder;
					if (docH) {
						const skElt = docH.getStruct(ctx.wedAnchor) as SkRuleNode;
						if (skElt) {
							const eltDef = skElt.contentRule.findRule((r) => r.structType === ENodeType.element) as SkRuleElt;
							if (eltDef) nodeName = eltDef.matcher.startName;
						}
					}
				}
				const md = WED.getModesForName(modes, nodeName);
				if (nodeName) return WedModel.findModel(this.eltModels.get(variants ? variants[nodeName] || nodeName : nodeName), md, null, ctx, selector, params) || WedModel.findModel(this.genericEltModel, md, null, ctx, selector, params);
				return WedModel.findModel(this.genericEltModel, md, null, ctx, selector, params);
			case ENodeType.text:
				return WedModel.findModel(this.textModel, WED.getModesForText(modes), null, ctx, selector, params);
			case ENodeType.comment:
				return WedModel.findModel(this.commentModel, WED.getModesForComment(modes), null, ctx, selector, params);
			default:
				throw Error(`Unknown nodeType ${nodeType} in findModelForVirtual.`);
			}
		}
	}

	/** Importe les IWedletModel d'un autre WedModel. */
	importFrom(wed: WedModel) {
		this.docModel = WedModel.importModel(this.docModel, wed.docModel);
		this.genericEltModel = WedModel.importModel(this.genericEltModel, wed.genericEltModel);
		this.genericAttrModel = WedModel.importModel(this.genericAttrModel, wed.genericAttrModel);
		this.textModel = WedModel.importModel(this.textModel, wed.textModel);
		this.commentModel = WedModel.importModel(this.commentModel, wed.commentModel);
		for (const [bindKey, otherModel] of wed.eltModels) {
			this.eltModels.set(bindKey, WedModel.importModel(otherModel, this.eltModels.get(bindKey)));
		}
		for (const [bindKey, otherModel] of wed.attrModels) {
			this.attrModels.set(bindKey, WedModel.importModel(otherModel, this.attrModels.get(bindKey)));
		}
		for (const k in wed.jsLibs) {
			if (!this.jsLibs[k]) this.jsLibs[k] = wed.jsLibs[k];
		}
		this.reg.copyFrom(wed.reg);
		// for (let [name, otherModel] of wed.structModels) {
		//     this.structModels.set(name, WedModel.importModel(otherModel, this.structModels.get(name)));
		// }
	}

	protected static injectModel(newModel: ModelRef, rootModel: ModelRef): ModelRef {
		if (rootModel == null) return newModel;
		if (rootModel.priority <= newModel.priority) {
			newModel.next = rootModel;
			return newModel;
		}
		let prevModel = rootModel;
		while (prevModel.next && prevModel.next.priority > newModel.priority) prevModel = prevModel.next;
		const nextModel = prevModel.next;
		prevModel.next = newModel;
		newModel.next = nextModel;
		return rootModel;
	}


	protected static findModel(rootModel: ModelRef, modes: string[], node: IJmlNode | string, ctx: WedMgr | IWedlet, selector: IWedSelector, params: any): IWedletModel {
		if (rootModel == null) return null;
		for (let i = 0; i < modes.length; i++) {
			const result = WedModel.findModelByMode(modes[i], rootModel, node, ctx, selector, params);
			if (result) return result;
		}
		return null;
	}

	protected static findModelByMode(mode: string, rootModel: ModelRef, node: IJmlNode | string, ctx: WedMgr | IWedlet, selector: IWedSelector, params: any): IWedletModel {
		while (rootModel && rootModel.model.modes.indexOf(mode) < 0) rootModel = rootModel.next;
		if (!rootModel) return null;
		if (selector == null && rootModel.selector == null) return rootModel.model;
		let bestModelH = rootModel;
		let bestScore = WedModel.computeScore(node, ctx, bestModelH, selector, params);
		if (bestScore === WedModel.SELECTOR_PERFECT_MATCH) return bestModelH.model;
		let modelH = rootModel.next;
		for (; ;) {
			while (modelH && modelH.model.modes.indexOf(mode) < 0) modelH = modelH.next;
			if (!modelH) break;
			const newScore = WedModel.computeScore(node, ctx, modelH, selector);
			if (newScore > bestScore) {
				if (newScore === WedModel.SELECTOR_PERFECT_MATCH) return modelH.model;
				bestScore = newScore;
				bestModelH = modelH;
			}
			modelH = modelH.next;
		}
		return bestScore != WedModel.SELECTOR_REJECT ? bestModelH.model : null;
	}

	/** Calcul d'un score de matching avec les selectors. */
	protected static computeScore(node: IJmlNode | string, ctx: WedMgr | IWedlet, modelH: ModelRef, callerSelector?: IWedSelector, params?: any): number {
		let score = WedModel.SELECTOR_PERFECT_MATCH;
		if (callerSelector) {
			score = callerSelector(modelH.model, node, ctx, params);
		}
		if (modelH.selector) {
			score = Math.min(score, modelH.selector(modelH.model, node, ctx, params));
		}
		return score;
	}

	protected static importModel(root: ModelRef, other: ModelRef) {
		while (other) {
			root = WedModel.injectModel(other.clone(), root);
			other = other.next;
		}
		return root;
	}
}

/**
 * Modèle (et factory) d'un IWedlet qui est un fragment d'éditeur.
 * Les modèles de wedlets doivent être enregistrés : WED.registerWedletModel("Box", BoxModel);
 * et posseder un constructeur sans paramètre.
 */
export interface IWedletModel {

	/**
	 * Type de noeuds sur lequel ce IWedletModel peut être exploité.
	 * EUnknownNodeType.unknown est possible avec NoneModel (cf <wed:bind nodeType="all" wedlet="None"/>).
	 */
	nodeType: ENodeType | EUnknownNodeType

	/**
	 * Nom de l'élément ou de l'attribut sur lequel ce IWedletModel peut être exploité.
	 * null pour les noeuds, text, comment, document, et pour les éléments et attributs
	 * génériques.
	 */
	nodeName?: string

	/** = nodeName ou combinaison du nodeName avec un identifiant de variante séparé par un |. */
	bindKey?: string

	/** Libellé (localisé) du noeud sur lequel ce IWedletModel peut être exploité. */
	nodeLabel?: string

	/** Modes (à la xslt) au sein desquels ce modèle peut-être exploité. */
	modes?: string[]

	perms?: IWedPerms

	/**
	 * Configuration du modèle, correspond à l'élément "wed:bind" ou "wed:display" du fichier wed.
	 */
	readonly config?: Element

	initModel(config: Element): void

	/**
	 * Crée le wedlet racine d'un éditeur.
	 * Utilisé par le WedMgr.
	 *
	 * @param parent Noeud HTML container de l'éditeur
	 * @param insertBefore Noeud avant lequel injecter ce widget.
	 * @param wedMgr Gestionnaire central qui fait le pont avec l'éditeur, le document édité, etc.
	 */
	createRootWedlet?(parent: Element, insertBefore: Node, wedMgr: WedMgr): IRootWedlet | null

	/**
	 * Création d'un wedlet fils dans le contexte parent d'un IWedlet donné.
	 * @return Retourne null si modèle "None".
	 */
	createWedlet?(parent: IParentWedlet, displayContext?: IWedDisplayElt): IChildWedlet | null

	/**
	 * Création à la volée d'un IWedletModel fournissant le contexte graphique nécessaire
	 * à l'affichage d'un fragment fils (diff, fragment exclus...).
	 *
	 * @param insertCtx wedlet de ce IWedletModel
	 */
	buildModelForFragment?(insertCtx: IWedlet): IWedletModel

	/** Si le bind est de type élément, peut fournir un indice pour l'odonnancement graphique des attributs. */
	attsOrder?: string[] | null
}

export interface IWedPerms {
	cascadRead?: string | string[]
	localWrite?: boolean | string | string[]
	cascadWrite?: boolean | string | string[]
}

export interface IWedTheme {
	themeContext?: RegExp
	themeVars: Dict<string>
}

/**
 * Fonction de sélection du 'meilleur' IWedletModel pour un noeud du document.
 *
 * Le IWedletModel sélectionné sera celui qui a obtenu le score le plus élevé.
 * Ces selectors sont utilisés à deux niveaux :
 * - un IWedSelector déclaré à l'enregistrement du IWedletModel qui pourra se comporter dynamiquement
 *   en fonction du contexte passé en paramètre (défini dans la balise wed:bind).
 * - un IWedSelector passé par le contexte appelant lors de la recherche d'un IWedletModel.
 */
export interface IWedSelector {

	/**
	 *
	 * @param wedModel
	 * @param node Noeud courant pour sélectionnner le 'meilleur' IWedletModel :
	 *          element, comment, text : noeud courant du matching (IJmlNode).
	 *          attribut : valeur de l'attribut (string).
	 *          document : null.
	 *          bind virtuel : null.
	 *        ATTENTION : une sélection contextuelle à l'environnement (attributs d'un élément...)
	 *        exige dans le wedlet une gestion manuelle de la détection des changements impliquant un changement de binding.
	 * @param ctx Contexte parent d'appel.
	 * @param params Paramètres d'appel par le contexte parent.
	 * @return Retourne un score entre 0 (WedModel.SELECTOR_REJECT) et 100 (WedModel.SELECTOR_PERFECT_MATCH)
	 */
	(this: void, wedModel: IWedletModel, node: IJmlNode | string, ctx: WedMgr | IWedlet, params: any): number;
}

/** Propriétés d'un <wed:slot/> */
export interface IWedSlotElt extends Element {
	/** Affectation automatique du nom du slot associé. */
	wedSlotName: string;

	/** wed:children de ce slot. */
	wedChildrenElts?: IWedChildrenElt[];
}

export function isWedSlotElt(elt: Node): elt is IWedSlotElt {
	return DOM.IS_element(elt) && elt.localName === "slot" && elt.namespaceURI === WED.WED_NS;
}

/** Propriétés d'un <wed:children/> */
export interface IWedChildrenElt extends Element {

	/** Critères de sélection des noeuds à insérer dans ce children. */
	wedMatcher: IWedMatcher

	wedMatchStrict: boolean

	/** Variantes de l'identifiant du bind à partir du name de l'elt ou de l'attr. */
	wedVariants?: Dict<string>

	/**
	 * Restrictions de la recherche du modèles aux modes spécifiés pour les noeuds fils. Par défaut, recherche sur le mode "default".
	 * Les modes sont indexés par le nodeName du noeud fils recherché ("!" pour un comment, "#" pour un noeud texte).
	 * Si la recherche par le nodeName exact n'aboutit pas, on prend les modes par défaut indexé par "".
	 */
	wedModesNodes?: Dict<string[]>

	/**
	 * Restrictions de la recherche du modèles aux modes spécifiés pour les attributs. Par défaut, recherche sur le mode "default".
	 * Les modes sont indexés par le nodeName de l'attribut recherché.
	 * Si la recherche par le nodeName exact n'aboutit pas, on prend les modes par défaut indexé par "".
	 */
	wedModesAtts?: Dict<string[]>

	/**
	 * Json de propriétés permettant aux wedlets appelés de se comporter en fonction de ces paramètres de l'appel parent.
	 * Ces paramètres sont aussi utilisable par les IWedSelector associés aux binds.
	 */
	wedParams?: any

	/** Nom du slot à reporter dans les éléments insérés. */
	wedSlotName: string

	/**
	 * Sélecteur du modèle lié au contexte d'appel (limitant aux types Box, Txt, Otl...).
	 * Propriété renseignée par IWedletModel.initModel().
	 */
	wedSelector?: IWedSelector

	/**
	 * Modèles à afficher dans ce children (et à privilégier sur les modèles du registre lors du binding des noeuds).
	 */
	wedDisplays?: IWedDisplayElt[]

	/** Affichage d'un bind par défaut en mode virtuel (si aucun contenu réel n'est affecté à ce children). */
	wedDefaultDisplay?: IWedDefaultDisplay

	/**
	 * Modèles à privilégier sur les modèles du registre central lors du binding des noeuds.
	 */
	wedPreferedModels?: IWedletModel[]

}

export const isWedChildrenElt = function (elt: Node): elt is IWedChildrenElt {return elt && DOM.IS_element(elt) && elt.localName === "children" && elt.namespaceURI === WED.WED_NS} as INodeFilter<IWedChildrenElt>;

/**
 * Propriétés d'un <wed:display/>.
 * Les éléments <wed:display/> sont privilégiés sur les <wed:bind> pour le matching d'un noeud.
 */
export interface IWedDisplayElt {
	/** Si renseigné, on utilise ce modèle à afficher. */
	wedletModel?: IWedletModel;
	/** Sinon, on recherche dans le registre le modèle via wedNodeType / wedNodeName et on l'insère . */
	wedNodeType?: number;
	wedNodeName?: string;
}

/**
 * Display par défaut issu d'un <wed:children defaultDisplay="x"/>.
 * Syntaxe de @defaultDisplay: '*', '@', '#', '@attName', 'eltName'.
 */
interface IWedDefaultDisplay extends IWedDisplayElt {
}

class WedDefaultDisplay implements IWedDefaultDisplay {
}

export function isWedDefaultDisplay(display: IWedDisplayElt): display is IWedDefaultDisplay {
	return display instanceof WedDefaultDisplay; // && elt.namespaceURI === WED.WED_NS;
}

function buildDefaultDisplay(children: IWedChildrenElt): IWedDefaultDisplay {
	const defaultDisplay = children.getAttribute("defaultDisplay");
	if (!defaultDisplay) return null;
	const result: IWedDefaultDisplay = new WedDefaultDisplay();
	if (defaultDisplay === '*') {
		result.wedNodeType = ENodeType.element;
	} else if (defaultDisplay === '@') {
		result.wedNodeType = Node.ATTRIBUTE_NODE;
	} else if (defaultDisplay === '#') {
		result.wedNodeType = ENodeType.text;
	} else if (defaultDisplay.charAt(0) === '@') {
		result.wedNodeType = Node.ATTRIBUTE_NODE;
		result.wedNodeName = defaultDisplay.substr(1);
	} else {
		result.wedNodeType = ENodeType.element;
		result.wedNodeName = defaultDisplay;
	}
	return result;
}

/**
 * Règle de matching d'un <wed:children select="...."/> sur la source.
 * Ces propriétés sont disponibles dès qu'un fichier Wed est chargé.
 * Syntaxe d'une règle de matching: '*', '@', '#', '!', '@attName', 'eltName'.
 * Plusieurs règles séparées par un espace est une union (OR).
 */
export interface IWedMatcher {
	matchNode(node: IJmlNode): boolean;

	matchAttr(attrName: string): boolean;

	matchStruct(struct: ISkStructDef): boolean;

	/** permet d'appliquer ces règles de matching du plus précis au plus générique pour trouver le bon elt children. */
	priorityMatcher: number;
}


/**
 * Namespace contenant :
 * - le registre statique des fichiers WED (WedModel) chargés, cf loadWedModel(url: string|URL)
 * - des objets et fonctions utilitaires pour le WedModel.
 */
export namespace WED {
	export const WED_NS = "scenari.eu:wed";

	/** Fetch un fichier WED et le charge. */
	export function loadWedModel(endPoint: IEndPoint, resolver?: IPathResolver): Promise<WedModel> {
		const result = _cache.get(endPoint.url);
		if (result) return result;
		const promise = endPoint.fetchDom(null).then((doc) => buildWedModel(doc, resolver));
		_cache.set(endPoint.url, promise);
		return promise;
	}

	/** Enregistrement des IWedletModel. */
	export function registerWedletModel(name: string, ctor: Constructor<IWedletModel>) {
		_registry.set(name, ctor);
	}

	export function isModelMatchNode(model: IWedletModel, node: IJmlNode): boolean {
		if (model.nodeType === EUnknownNodeType.unknown) return true;
		if (model.nodeType !== JML.jmlNode2nodeType(node)) return false;
		if (model.nodeName) return (node as IJmlObj)[''] === model.nodeName;
		return true;
	}

	export function isModelMatchAttr(model: IWedletModel, attrName: string): boolean {
		if (model.nodeType !== ENodeType.attribute) return false;
		if (model.nodeName) return attrName === model.nodeName;
		return true;
	}

	export function isDisplayMatchAttr(display: IWedDisplayElt, attrName: string): boolean {
		if (display.wedNodeType !== ENodeType.attribute) return false;
		if (display.wedNodeName) return attrName === display.wedNodeName;
		return true;
	}

	export function isDisplayMatchNode(display: IWedDisplayElt, node: IJmlNode): boolean {
		if (display.wedNodeType !== JML.jmlNode2nodeType(node)) return false;
		if (display.wedNodeName) return (node as IJmlObj)[''] === display.wedNodeName;
		return true;
	}

	export const SORT_ChildrenElts = function (o1: IWedChildrenElt, o2: IWedChildrenElt): number {
		return o2.wedMatcher.priorityMatcher - o1.wedMatcher.priorityMatcher;
	};

	/** Modes par défaut. */
	export const DEFAULT_MODES = Object.freeze([""]) as string[];

	/** Intègre un WED. */
	async function buildWedModel(doc: Document & IEndPointHolder, resolver: IPathResolver): Promise<WedModel> {
		const wed = new WedModel();
		let depWeds: Promise<WedModel>[];
		let depLibs: Promise<void>[];
		const rootElt = importAndCleanupConfig(doc.documentElement, resolver);
		for (let elt = rootElt.firstElementChild; elt; elt = elt.nextElementSibling) {
			if (elt.namespaceURI === WED_NS) {
				switch (elt.localName) {
				case "display" : //FIXME Ne devrait pas être autorisé (pas d'usage), à revoir pb avec BoxHidden
				case "bind" : {
					const childrenElts = elt.querySelectorAll("children");
					//1ère étape : identification de tous les elt children.
					for (let i = 0; i < childrenElts.length; i++) {
						const childrenElt = childrenElts[i] as IWedChildrenElt;
						const parent = childrenElt.parentNode;
						if (isWedSlotElt(parent)) {
							if (!parent.wedSlotName) {
								parent.wedSlotName = i.toString();
								parent.wedChildrenElts = [];
							}
							childrenElt.wedSlotName = parent.wedSlotName;
							parent.wedChildrenElts.push(childrenElt);
						} else {
							childrenElt.wedSlotName = i.toString();
						}
						childrenElt.wedMatcher = buildMatcherVariants(childrenElt.getAttribute("select"), childrenElt);
						childrenElt.wedMatchStrict = childrenElt.hasAttribute('strict');
						childrenElt.wedDefaultDisplay = buildDefaultDisplay(childrenElt);
						const params = childrenElt.getAttribute("params");
						if (params) childrenElt.wedParams = JSON.parse(params);
					}
					//2eme étape : construction du contenu de tous les elt children.
					for (let i = 0; i < childrenElts.length; i++) {
						const childrenElt = childrenElts[i] as IWedChildrenElt;
						for (let ch = childrenElt.firstElementChild; ch; ch = ch.nextElementSibling) {
							if (ch.namespaceURI !== WED_NS) continue;
							switch (ch.localName) {
							case "display" :
								const displayElt = ch as IWedDisplayElt;
								if (!childrenElt.wedDisplays) childrenElt.wedDisplays = [displayElt];
								else childrenElt.wedDisplays.push(displayElt);
								if (ch.hasAttribute("wedlet")) {
									displayElt.wedletModel = buildBind(ch);
									if (!childrenElt.wedPreferedModels) childrenElt.wedPreferedModels = [displayElt.wedletModel];
									else childrenElt.wedPreferedModels.push(displayElt.wedletModel);
								} else {
									[displayElt.wedNodeType, displayElt.wedNodeName] = parseNodeDef(ch);
									initModesForChild(childrenElt, displayElt.wedNodeType, displayElt.wedNodeName, ch);
								}
								break;
							case "bind" :
								if (ch.hasAttribute("wedlet")) {
									//Déclaration d'un bind local
									if (!childrenElt.wedPreferedModels) childrenElt.wedPreferedModels = [];
									childrenElt.wedPreferedModels.push(buildBind(ch));
								} else {
									//Déclaration de wed:bind pour un callModes personnalisé.
									const [nodeType, nodeName] = parseNodeDef(ch);
									initModesForChild(childrenElt, nodeType, nodeName, ch);
								}
								break;
							default :
								throw Error(`Unknown child tag ${ch.nodeName} in ${DOM.ser(childrenElt)} in ${doc.baseEndPoint}`);
							}
						}
						//on finalise la constrution des modes avec le mode par défaut.
						const defaultModes = parseModes(childrenElt.getAttribute("callModes"));
						if (defaultModes !== null) {
							if (childrenElt.wedModesNodes) {
								childrenElt.wedModesNodes[''] = defaultModes;
								if (childrenElt.wedModesAtts) childrenElt.wedModesAtts[''] = defaultModes;
								else childrenElt.wedModesAtts = {'': defaultModes};
							} else {
								childrenElt.wedModesNodes = {'': defaultModes};
								if (childrenElt.wedModesAtts) childrenElt.wedModesAtts[''] = defaultModes;
								else childrenElt.wedModesAtts = childrenElt.wedModesNodes;
							}
						}
					}
					const model = buildBind(elt);
					model.modes = parseModes(elt.getAttribute("modes")) || WED.DEFAULT_MODES;
					const selector = elt.getAttribute("selector");
					let selectorFn = selector ? new Function("wedletModel", "node", "ctx", "params", selector) as IWedSelector : null;
					const isParam = elt.getAttribute("isParam");
					if (isParam) selectorFn = combineSelectors(selectorFn, (wedModel: IWedletModel, node: IJmlNode | string, ctx: WedMgr | IWedlet, params: any): number => {
						return params && params[isParam] ? WedModel.SELECTOR_PERFECT_MATCH : WedModel.SELECTOR_REJECT;
					});
					wed.addNodeModel(model, parseInt(elt.getAttribute("priority"), 10) || 0, selectorFn);
					break;
				}
				case "import" : {
					if (!depWeds) depWeds = [];
					depWeds.push(loadWedModel(doc.baseEndPoint.resolve(elt.getAttribute("src")), resolver));
					break;
				}
				case "jslib" : {
					if (!depLibs) depLibs = [];
					const jsEndPoint = doc.baseEndPoint.resolve(elt.getAttribute("src"));
					depLibs.push(jsEndPoint.importJs().then((mod: any) => {
						const key = elt.getAttribute("key");
						if (key) wed.jsLibs[key] = mod;
						if (mod.jslibAsyncInit) return mod.jslibAsyncInit(jsEndPoint, elt, wed.reg);
					}));
					break;
				}
				case "jslibSync" : {
					const jsEndPoint = doc.baseEndPoint.resolve(elt.getAttribute("src"));
					const mod = await jsEndPoint.importJs();
					const key = elt.getAttribute("key");
					if (key) wed.jsLibs[key] = mod;
					if (mod.jslibAsyncInit) {
						if (!depLibs) depLibs = [];
						depLibs.push(mod.jslibAsyncInit(jsEndPoint, elt, wed.reg));
					}
					break;
				}
				case "theme" : {
					const regexp = elt.getAttribute("themeContextRegexp");
					const dict = Object.create(null) as Dict<string>;
					for (let r of elt.textContent.split(";")) {
						const sep = r.indexOf(':');
						if (sep > 0) dict[r.substring(0, sep).trim()] = r.substring(sep + 1).trim();
					}
					wed.themes.push({
						themeContext: regexp ? new RegExp(regexp) : null,
						themeVars: dict
					});
					break;
				}
				case "shadowSkin" : {
					if (elt.hasAttribute("overlay")) {
						wed.reg.overlaySkin(elt.getAttribute("tag"), parseFloat(elt.getAttribute("order")) || REG.LEVELAUTH_MODEL, elt.textContent);
					} else {
						wed.reg.registerSkin(elt.getAttribute("tag"), REG.LEVELAUTH_MODEL, elt.textContent);
					}
					break;
				}
				default :
					throw Error(`Unknown wed tag '${DOM.debug(elt)}' in ${doc.baseEndPoint && doc.baseEndPoint.url}`);
				}
			}
		}
		if (depLibs) await Promise.all(depLibs);
		if (depWeds) await Promise.all(depWeds).then(weds => {
			weds.forEach((w: WedModel) => {
				wed.importFrom(w);
			});
		});
		return wed;
	}

	/** Résoud wed:clone, supprime les noeuds white spaces et commentaires. */
	function importAndCleanupConfig(elt: Element, resolver: IPathResolver): Element {
		//console.log("BEFORE importAndCleanupConfig:::::\n"+DOM.debug(elt));
		let ids: Dict<Element>;
		let toClone;
		const tw = elt.ownerDocument.createNodeIterator(elt);
		let previous = tw.nextNode();
		let next;
		while ((next = tw.nextNode()) || previous) {
			if (previous instanceof Element) {
				if (previous.localName === 'clone' && previous.namespaceURI === WED_NS) {
					if (!toClone) toClone = [previous];
					else toClone.push(previous);
				} else {
					const id = previous.getAttributeNS(WED_NS, 'id');
					if (id) {
						if (!ids) ids = {};
						ids[id] = previous;
						previous.removeAttributeNS(WED_NS, 'id');
					}
					if (resolver != null && previous.namespaceURI !== WED_NS) {
						let path = previous.getAttribute("src");
						if (path) previous.setAttribute("src", resolver.resolvePath(path));
						path = previous.getAttribute("href");
						if (path) previous.setAttribute("href", resolver.resolvePath(path));
						path = previous.getAttribute("icon");
						if (path) previous.setAttribute("icon", resolver.resolvePath(path));
						const text = previous.getAttribute("style");
						if (text) previous.setAttribute("style", resolver.resolveInText(text));
					}
				}
			} else if (previous.nodeType === ENodeType.text) {
				if (DOM.WHITESPACES.test(previous.nodeValue)) {
					//supprime les noeuds textes vides.
					previous.parentNode.removeChild(previous);
				} else if (resolver != null) {
					const parentNm = previous.parentElement.localName;
					if (parentNm === 'script' || parentNm === 'style' || parentNm === 'shadowSkin' || parentNm === 'theme') {
						previous.nodeValue = resolver.resolveInText(previous.nodeValue);
					}
				}
			} else {
				//supprime les autres type de noeuds commentaires et PI.
				previous.parentNode.removeChild(previous);
			}
			previous = next;
		}
		if (toClone) {
			//on clone en 1er les noeuds les plus profonds (pb d'un clone contenant un clone)
			toClone.forEach((elt: Element) => {
				const target = ids ? ids[elt.getAttribute("refId")] : null;
				if (!target) throw Error(`Target in <wed:clone refId="${elt.getAttribute("refId")}"/> does not exist.`);
				(elt as any).cloneTarget = target;
				(elt as any).cloneDeep = DOM.computeDepth(target);
			});
			toClone.sort((e1: any, e2: any) => e1.cloneDeep - e2.cloneDeep);
			toClone.forEach((elt: Element) => {
				elt.parentNode.insertBefore((elt as any).cloneTarget.cloneNode(true), elt);
				elt.remove();
			});
		}
		const imported = document.adoptNode(elt);
		(document.head.appendChild(document.createElement("template")) as HTMLTemplateElement).content.appendChild(imported);
		return imported;
	}

	function initModesForChild(childrenElt: IWedChildrenElt, nodeType: ENodeType, nodeName: string, ch: Element) {
		const modes = parseModes(ch.getAttribute("callModes"));
		if (modes) {
			if (nodeType === ENodeType.attribute) {
				if (!childrenElt.wedModesAtts) childrenElt.wedModesAtts = {};
				childrenElt.wedModesAtts[nodeName || '*'] = modes;
			} else {
				if (!nodeName) nodeName = nodeType == ENodeType.element ? '*' : nodeType == ENodeType.comment ? '!' : nodeType == ENodeType.text ? '#' : '';
				if (!childrenElt.wedModesNodes) childrenElt.wedModesNodes = {};
				childrenElt.wedModesNodes[nodeName] = modes;
			}
		}
	}

	export function parseModes(modes: string): string[] | null {
		if (!modes) return null;
		const array = modes.split(" ");
		const idx = array.indexOf(".");
		if (idx >= 0) array[idx] = "";
		return array;
	}

	export function getModesForComment(wedModes: Dict<string[]>): string[] {
		if (!wedModes) return WED.DEFAULT_MODES;
		return wedModes['!'] || wedModes[''] || WED.DEFAULT_MODES;
	}

	export function getModesForText(wedModes: Dict<string[]>): string[] {
		if (!wedModes) return WED.DEFAULT_MODES;
		return wedModes['#'] || wedModes[''] || WED.DEFAULT_MODES;
	}

	/** '*' any name (elt ou att), '' mode par défaut global. */
	export function getModesForName(wedModes: Dict<string[]>, nodeName: string): string[] {
		if (!wedModes) return WED.DEFAULT_MODES;
		return wedModes[nodeName || '*'] || wedModes['*'] /*nodeName pas nul mais pas trouvé*/ || wedModes[''] || WED.DEFAULT_MODES;
	}

	const _cache = new Map<string, Promise<WedModel>>();

	const _registry = new Map<string, Constructor<IWedletModel>>();


	export function newWedletModel(wedletName?: string): IWedletModel {
		const ctor: Constructor<IWedletModel> = _registry.get(wedletName);
		if (!ctor) throw Error(`Wedlet name unknown: ${wedletName}`);
		return new ctor();
	}

	export function buildBind(elt: Element, defaultWedlet?: string): IWedletModel {
		const model = newWedletModel(elt.getAttribute("wedlet") || defaultWedlet);
		[model.nodeType, model.nodeName, model.bindKey] = parseNodeDef(elt);
		model.perms = parsePerms(elt);
		model.initModel(elt);
		return model;
	}

	function parseNodeDef(elt: Element): [number, string, string] {
		let name = elt.getAttribute("eltName");
		if (name !== null) {
			_parseNodeDefResult[0] = ENodeType.element;
			_parseNodeDefResult[1] = name;
			const variant = elt.getAttribute("variant");
			_parseNodeDefResult[2] = variant ? name + '|' + variant : name;
		} else {
			name = elt.getAttribute("attName");
			if (name) {
				_parseNodeDefResult[0] = ENodeType.attribute;
				_parseNodeDefResult[1] = name;
				const variant = elt.getAttribute("variant");
				_parseNodeDefResult[2] = variant ? name + '|' + variant : name;
			} else {
				_parseNodeDefResult[1] = null;
				_parseNodeDefResult[2] = null;
				switch (elt.getAttribute("nodeType")) {
				case "element":
					_parseNodeDefResult[0] = ENodeType.element;
					break;
				case "attribute":
					_parseNodeDefResult[0] = ENodeType.attribute;
					break;
				case "text":
					_parseNodeDefResult[0] = ENodeType.text;
					break;
				case "comment":
					_parseNodeDefResult[0] = ENodeType.comment;
					break;
				case "document":
					_parseNodeDefResult[0] = ENodeType.document;
					break;
				case "all":
					_parseNodeDefResult[0] = EUnknownNodeType.unknown;
					break;
				default:
					throw Error(`Wed nodeType unknown ${DOM.ser(elt)}`);
				}
			}
		}
		return _parseNodeDefResult;
	}

	const _parseNodeDefResult: [number, string, string] = [0, null, null];

	export function parsePerms(elt: Element): IWedPerms | null {
		const read = elt.getAttribute("readPerms");
		const write = elt.getAttribute("writePerms");
		const localWrite = elt.getAttribute("localWritePerms");
		if (read || write || localWrite) {
			const p = Object.create(null) as IWedPerms;
			if (read) p.cascadRead = read.indexOf(' ') > 0 ? read.split(' ') : read;
			if (write) {
				if (write === '#yes') p.cascadWrite = true;
				else if (write === '#no') p.cascadWrite = false;
				else p.cascadWrite = write.indexOf(' ') > 0 ? write.split(' ') : write;
				p.localWrite = p.cascadWrite;
			}
			if (localWrite) {
				if (localWrite === '#yes') p.localWrite = true;
				else if (localWrite === '#no') p.localWrite = false;
				else p.localWrite = localWrite.indexOf(' ') > 0 ? localWrite.split(' ') : localWrite;
			}
			return p;
		}
		return null;
	}

	export function buildMatcherVariants(matchExp: string, childrenElt?: IWedChildrenElt): IWedMatcher {
		if (!matchExp) return MATCH_ALL;
		if (matchExp === "* # !") return MATCH_ALL_NOATTR; //optimisation.
		const parts = matchExp.split(' ');
		if (parts.length === 1) {
			return buildSingleMatcher(parts[0], childrenElt);
		} else {
			let prioity: number = Infinity;
			const matchers = parts.map(part => {
				const m = buildSingleMatcher(part, childrenElt);
				prioity = Math.min(prioity, m.priorityMatcher);
				return m;
			});
			return new UnionMatcher(matchers, prioity);
		}
	}

	function buildSingleMatcher(part: string, childrenElt?: IWedChildrenElt): IWedMatcher {
		if (part === '@') return MATCH_ATTRIBUTES;
		if (part === '*') return MATCH_ELEMENTS;
		if (part === '#') return MATCH_TEXT;
		if (part === '!') return MATCH_COMMENTS;
		const isAttr = part.charAt(0) === '@';
		const variantSep = part.indexOf('|');
		if (variantSep > 0) {
			if (childrenElt) {
				const variant = part;
				part = part.substr(isAttr ? 1 : 0, variantSep);
				if (!childrenElt.wedVariants) childrenElt.wedVariants = {};
				childrenElt.wedVariants[part] = variant;
			} else {
				part = part.substr(isAttr ? 1 : 0, variantSep);
			}
		} else if (isAttr) part = part.substring(1);
		return isAttr ? new AttrMatcher(part) : new EltMatcher(part);
	}

	const MATCH_ALL: IWedMatcher = {
		priorityMatcher: 0,
		matchNode(node: IJmlNode): boolean {return true},
		matchAttr(attrName: string): boolean {return true},
		matchStruct(struct: ISkStructDef): boolean {return true}
	};

	const MATCH_ALL_NOATTR: IWedMatcher = {
		priorityMatcher: 0,
		matchNode(node: IJmlNode): boolean {return true},
		matchAttr(attrName: string): boolean {return false},
		matchStruct(struct: ISkStructDef): boolean {return !struct.structMatch(ENodeType.attribute)}
	};

	const MATCH_ATTRIBUTES: IWedMatcher = {
		priorityMatcher: 1,
		matchNode(node: IJmlNode): boolean {return false},
		matchAttr(attrName: string): boolean {return attrName != null},
		matchStruct(struct: ISkStructDef): boolean {return struct.structMatch(ENodeType.attribute)}
	};

	const MATCH_ELEMENTS: IWedMatcher = {
		priorityMatcher: 1,
		matchNode(node: IJmlNode): boolean {return JML.isElt(node)},
		matchAttr(attrName: string): boolean {return false},
		matchStruct(struct: ISkStructDef): boolean {return struct.structMatch(ENodeType.element)}
	};

	const MATCH_COMMENTS: IWedMatcher = {
		priorityMatcher: 1,
		matchNode(node: IJmlNode): boolean {return JML.jmlNode2nodeType(node) === ENodeType.comment},
		matchAttr(attrName: string): boolean {return false},
		matchStruct(struct: ISkStructDef): boolean {return struct.structMatch(ENodeType.comment)}
	};

	const MATCH_TEXT: IWedMatcher = {
		priorityMatcher: 1,
		matchNode(node: IJmlNode): boolean {return JML.jmlNode2nodeType(node) === ENodeType.text},
		matchAttr(attrName: string): boolean {return false},
		matchStruct(struct: ISkStructDef): boolean {return struct.structMatch(ENodeType.text)}
	};

	class EltMatcher implements IWedMatcher {
		get priorityMatcher() {return 10}

		constructor(public name: string) {}

		matchNode(node: IJmlNode) {return JML.isElt(node) && JML.jmlNode2name(node) === this.name;}

		matchAttr(attrName: string): boolean {return false}

		matchStruct(struct: ISkStructDef): boolean {return struct.structMatch(ENodeType.element, this.name)}
	}

	class AttrMatcher implements IWedMatcher {
		get priorityMatcher() {return 10}

		constructor(public name: string) {}

		matchNode(node: IJmlNode) {return false}

		matchAttr(attrName: string): boolean {return this.name === attrName}

		matchStruct(struct: ISkStructDef): boolean {return struct.structMatch(ENodeType.attribute, this.name)}
	}

	class UnionMatcher implements IWedMatcher {
		constructor(public subMatchers: IWedMatcher[], public priorityMatcher: number) {}

		matchNode(node: IJmlNode) {return this.subMatchers.some(m => m.matchNode(node))}

		matchAttr(attrName: string): boolean {return this.subMatchers.some(m => m.matchAttr(attrName))}

		matchStruct(struct: ISkStructDef): boolean {return this.subMatchers.some(m => m.matchStruct(struct))}
	}

	export function combineSelectors(s1: IWedSelector, s2: IWedSelector): IWedSelector {
		if (s1 == null) return s2;
		if (s2 == null) return s1;
		return (wedModel: IWedletModel, node: IJmlNode | string, ctx: WedMgr | IWedlet, params: any) => Math.min(s1(wedModel, node, ctx, params), s2(wedModel, node, ctx, params));
	}

}

/** class interne pour gérer les chaines indexées des IWedletModel. */
class ModelRef {
	next: ModelRef;

	constructor(readonly model: IWedletModel, readonly priority: number, readonly selector: IWedSelector) {
	}

	clone(): ModelRef {
		return new ModelRef(this.model, this.priority, this.selector);
	}
}

/** Sélecteur pour les IWedletModel capables de créer un IRootWedlet (wedlet racine de l'éditeur) */
export const WED_ROOT_SELECTOR: IWedSelector = function (wedModel: IWedletModel, node: IJmlNode | string, ctx: WedMgr | IWedlet) {
	if (typeof wedModel.createRootWedlet === 'function') return WedModel.SELECTOR_PERFECT_MATCH;
	return WedModel.SELECTOR_REJECT;
};

/** Model de wedlet de base. */
export abstract class WedletModelBase implements IWedletModel {
	nodeType: number;
	nodeName: string;

	modes: string[];
	config: Element;

	initModel(config: Element) {this.config = config}

}

/**
 * Modèle de wedlet "None" éliminant totalement la création d'un wedlet et d'une représentation HTML.
 * A utiliser pour des attributs (xmlns, xml:space...).
 * Plus délicat pour des éléments ou commentairess si l'édition est active :
 * l'insertion de noeuds frères suivants n'est plus possible.
 */
export class NoneModel extends WedletModelBase {

	get isBoxFamily(): boolean {return true}

	createWedlet(parent: IParentWedlet, displayContext?: IWedDisplayElt): IChildWedlet | null {
		return null;
	}
}

WED.registerWedletModel("None", NoneModel);
